import { Alert, AlertTitle, Box, Button, Dialog, Typography } from "@mui/material";
import { LeadsManagementData } from "../hooks/useLeadsManagementData";
import { isReportableStatus } from "../constants";

interface ReportLeadsModalProps {
  data: LeadsManagementData;
}

const BODY_TEXT_SX = {
  fontFamily: "Roboto",
  fontWeight: 400,
  fontSize: 14,
  lineHeight: "143%",
  letterSpacing: "0.17px",
};

const TABLE_HEADER_SX = {
  fontFamily: "Roboto",
  fontWeight: 500,
  fontSize: 14,
  lineHeight: "24px",
  letterSpacing: "0.17px",
  color: "text.primary",
};

const BUTTON_SX = {
  fontFamily: "Roboto",
  fontWeight: 500,
  fontSize: 14,
  lineHeight: "24px",
  letterSpacing: "0.4px",
  borderRadius: 1,
  px: 2,
};

// Same layout as RejectLeadsModal, with an info (not warning) alert.
export default function ReportLeadsModal({ data }: ReportLeadsModalProps) {
  const { showConfirm, selectedLeads, cancelBulkReport, confirmBulkReport, isReporting, canReportLeads } = data;

  const reportableSelected = selectedLeads.filter((lead) => isReportableStatus(lead.status));
  const brandCounts = new Map<string, number>();
  reportableSelected.forEach((lead) => {
    brandCounts.set(lead.brand, (brandCounts.get(lead.brand) ?? 0) + 1);
  });
  const brandBreakdown = Array.from(brandCounts.entries())
    .map(([brand, count]) => ({ brand, count }))
    .sort((a, b) => b.count - a.count);

  return (
    <Dialog
      open={showConfirm}
      onClose={cancelBulkReport}
      maxWidth={false}
      PaperProps={{ sx: { borderRadius: 2, width: 552, maxWidth: "calc(100% - 64px)" } }}
    >
      <Box sx={{ px: "24px", pt: "16px", pb: "8px" }}>
        <Typography
          sx={{ fontFamily: "Roboto", fontWeight: 500, fontSize: 20, lineHeight: "160%", letterSpacing: "0.15px", color: "#000000DE" }}
        >
          Report Lead
        </Typography>
        <Alert
          severity="info"
          sx={{
            ...BODY_TEXT_SX,
            mt: "16px",
            p: "4px",
            alignItems: "flex-start",
            // Icon sits on the title's 24px line instead of MUI's default 7px top offset.
            "& .MuiAlert-icon": { p: 0, mt: "1px", mr: "12px", fontSize: 22, color: "info.main" },
            "& .MuiAlert-message": { p: 0 },
            borderRadius: "8px",
            border: (theme) => `1px solid ${theme.palette.info.light}`,
          }}
        >
          <AlertTitle
            sx={{ fontFamily: "Roboto", fontWeight: 500, fontSize: 16, lineHeight: "150%", letterSpacing: "0.15px", color: "#014361", mb: "4px" }}
          >Reporting to Brand</AlertTitle>
          <Typography sx={{ ...BODY_TEXT_SX, color: "#014361" }}>Reported leads cannot be undone.</Typography>
        </Alert>
      </Box>

      <Box sx={{ px: "24px", py: "8px" }}>
        <Box sx={{ maxHeight: 200, overflowY: "auto" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: "8px",
              py: "6px",
              borderBottom: (theme) => `1px solid ${theme.palette.grey[300]}`,
            }}
          >
            <Typography sx={TABLE_HEADER_SX}>Brand Name</Typography>
            <Typography sx={TABLE_HEADER_SX}>Leads</Typography>
          </Box>
          {brandBreakdown.map(({ brand, count }, index) => (
            <Box
              key={brand}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: "8px",
                py: "8px",
                borderTop: index === 0 ? "none" : (theme) => `1px solid ${theme.palette.grey[300]}`,
              }}
            >
              <Typography sx={{ ...BODY_TEXT_SX, color: "text.primary" }}>{brand}</Typography>
              <Typography sx={{ ...BODY_TEXT_SX, color: "text.primary" }}>{count}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, px: "24px", pt: "8px", pb: "16px" }}>
        <Button
          onClick={cancelBulkReport}
          disabled={isReporting}
          sx={{ ...BUTTON_SX, color: "secondary.main", "&:hover": { bgcolor: "action.hover" } }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={confirmBulkReport}
          disabled={!canReportLeads || isReporting || reportableSelected.length === 0}
          data-tracker="930"
          sx={{
            ...BUTTON_SX,
            bgcolor: "secondary.main",
            "&:hover": { bgcolor: "secondary.dark" },
          }}
        >
          {isReporting ? "Reporting…" : "Confirm"}
        </Button>
      </Box>
    </Dialog>
  );
}
